import { useState } from 'react'
import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { YlyGrid } from './shared/YlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useDlyOneYearData } from '../../hooks/useDlyOneYearData'
import { TEMP_SCALE } from './shared/weatherUtils'

const SLUG = 'dly-one-year-soil'

const DEPTHS = [
  { tsId: 'DLY_SOIL05AVG',  label: '5 cm'   },
  { tsId: 'DLY_SOIL10AVG',  label: '10 cm'  },
  { tsId: 'DLY_SOIL20AVG',  label: '20 cm'  },
  { tsId: 'DLY_SOIL50AVG',  label: '50 cm'  },
  { tsId: 'DLY_SOIL100AVG', label: '100 cm' },
]

function soilCellStyle(value) {
  if (value == null) return null
  const entry = TEMP_SCALE.find(e => value >= e.min)
  return entry ? { bg: entry.bg, text: entry.text } : null
}

function soilFormat(value) {
  if (value == null) return '—'
  return value.toFixed(1)
}

function SoilLegend() {
  return (
    <div className="shrink-0 flex flex-col gap-0.5">
      <div className="text-xs font-semibold text-gray-400 tracking-wide mb-1">°C</div>
      {TEMP_SCALE.map(({ label, bg }) => (
        <div key={label} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <span className="inline-block w-4 h-4 rounded-sm shrink-0" style={{ backgroundColor: bg }} />
          <span className="text-gray-600">{label}</span>
        </div>
      ))}
    </div>
  )
}

export function DlyOneYearSoilView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const [depthIdx, setDepthIdx] = useState(0)

  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const depth = DEPTHS[depthIdx]
  const { dataMap, isLoading, isError, error } = useDlyOneYearData(station?.gh_id, year, depth.tsId)

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {DEPTHS.map((d, i) => (
                <button
                  key={d.tsId}
                  onClick={() => setDepthIdx(i)}
                  className={`px-3 py-1.5 transition-colors ${
                    i === depthIdx
                      ? 'bg-blue-600 text-white font-medium'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && <p className="text-red-500 text-sm">{error?.message ?? 'Chyba při načítání dat'}</p>}
        {!isLoading && !isError && (
          <div className="flex-1 min-h-0 flex gap-6">
            <div className="flex-1 min-w-0 min-h-0">
              <YlyGrid year={year} dataMap={dataMap} getCellStyle={soilCellStyle} formatCell={soilFormat} orientation={orientation} />
            </div>
            <SoilLegend />
          </div>
        )}
      </div>
    </div>
  )
}
